import { Button } from './Button';
import { VscLoading } from 'react-icons/vsc';
import { BsStars } from 'react-icons/bs';
import { useProcessingSteps, DEFAULT_PROCESSING_STEPS } from '@core/hooks/useProcessingSteps';

interface ProcessingButtonProps {
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  submitButtonText: string;
  className?: string;
  isUploading?: boolean;
  uploadProgress?: number;
  steps?: string[];
  stepIntervalMs?: number;
}

export function ProcessingButton({
  onClick,
  disabled,
  isLoading,
  submitButtonText,
  className = '',
  isUploading = false,
  uploadProgress = 0,
  steps = DEFAULT_PROCESSING_STEPS,
  stepIntervalMs = 2500,
}: ProcessingButtonProps) {
  const isProcessingActive = isLoading && !isUploading;

  const { currentStepIndex, currentStep } = useProcessingSteps({
    isActive: isProcessingActive,
    steps,
    intervalMs: stepIntervalMs,
  });

  // State 2: Uploading (FileInput only)
  if (isUploading) {
    return (
      <Button
        onClick={onClick}
        disabled={true}
        className={`flex items-center border-none !gap-2 disabled:cursor-not-allowed bg-sky-500 text-white hover:bg-sky-500 disabled:bg-sky-500 disabled:text-white disabled:opacity-100 ${className}`}
      >
        <VscLoading className="animate-spin text-lg" />
        <span>Uploading {uploadProgress}%</span>
      </Button>
    );
  }

  // State 3: Processing with animated gradient + step labels
  if (isLoading) {
    return (
      <Button
        onClick={onClick}
        disabled={true}
        style={{ minWidth: '260px' }}
        className={`processing-button-active flex items-center border-none !gap-2 disabled:cursor-not-allowed text-white disabled:opacity-100 ${className}`}
      >
        {/* Branded 3-dot loader */}
        <span className="processing-dots flex items-center gap-[3px]">
          <span className="processing-dot" />
          <span className="processing-dot" />
          <span className="processing-dot" />
        </span>
        {/* Step label with fade-in-up on change */}
        <span key={currentStepIndex} className="processing-step-label">
          {currentStep}
        </span>
      </Button>
    );
  }

  // State 1: Idle
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center border-none !gap-2 disabled:cursor-not-allowed bg-sky-500 text-white hover:bg-sky-500 disabled:bg-sky-500 disabled:text-white ${className}`}
    >
      <BsStars className="text-lg" />
      <span>{submitButtonText}</span>
    </Button>
  );
}
